'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

import {
  AlertTriangle,
  RefreshCw,
  TrendingDown,
  Clock,
  Layers,
  BarChart2,
  CheckCircle2,
  XCircle,
  Hourglass,
  Activity,
  PhoneOff,
  TimerOff,
  ShieldX,
  Filter,
  History,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import Icon from '@/components/ui/AppIcon';
import LeadMessageHistoryModal from '@/components/LeadMessageHistoryModal';


// ─── Types ────────────────────────────────────────────────────────────────────

interface DeletedLeadLog {
  id: string;
  deleted_at: string;
  phone: string;
  lead_name: string | null;
  source: string | null;
  failure_reason: string;
  message_sid: string | null;
}

interface TrendPoint {
  label: string;
  failures: number;
}

interface SourceStat {
  source: string;
  count: number;
  fill: string;
}

interface CampaignStat {
  campaign: string;
  failureRate: number;
  total: number;
  failed: number;
}

interface RetryItem {
  id: string;
  phone: string;
  lead_name: string | null;
  source: string | null;
  failure_reason: string;
  deleted_at: string;
  retryStatus: 'pending' | 'retrying' | 'exhausted';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SOURCE_COLORS: Record<string, string> = {
  'MBBS Lighthouse': '#ef4444',
  Website: '#f97316',
  GMC: '#eab308',
  'Direct Call': '#8b5cf6',
  Referral: '#06b6d4',
  Other: '#6b7280',
};

function getSourceColor(source: string | null): string {
  if (!source) return '#6b7280';
  return SOURCE_COLORS[source] ?? '#6b7280';
}

function buildTrend(logs: DeletedLeadLog[]): TrendPoint[] {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function dateKey(d: Date) {
    return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]}`;
  }
  const buckets: Record<string, number> = {};
  const now = new Date();
  // Last 14 days
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    buckets[dateKey(d)] = 0;
  }
  logs.forEach((l) => {
    const d = new Date(l.deleted_at);
    const key = dateKey(d);
    if (key in buckets) buckets[key]++;
  });
  return Object.entries(buckets).map(([label, failures]) => ({ label, failures }));
}

function buildSourceStats(logs: DeletedLeadLog[]): SourceStat[] {
  const map: Record<string, number> = {};
  logs.forEach((l) => {
    const src = l.source ?? 'Other';
    map[src] = (map[src] ?? 0) + 1;
  });
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .map(([source, count]) => ({ source, count, fill: getSourceColor(source) }));
}

// Campaign stats are now fetched from Supabase in the component

function buildRetryQueue(logs: DeletedLeadLog[]): RetryItem[] {
  // Show the 10 most recent failures as "retry queue" items
  return logs.slice(0, 10).map((l, i) => ({
    ...l,
    retryStatus: i < 3 ? 'retrying' : i < 7 ? 'pending' : 'exhausted',
  }));
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[d.getMonth()];
  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${day} ${month}, ${hour12}:${minutes} ${ampm}`;
}

// ─── Failure Reason Filter ─────────────────────────────────────────────────────

type FailureReasonFilter = 'all' | 'invalid_number' | 'delivery_timeout' | 'carrier_rejection';

const FAILURE_REASON_OPTIONS: {
  value: FailureReasonFilter;
  label: string;
  icon: React.ElementType;
  color: string;
  activeBg: string;
  activeText: string;
  activeBorder: string;
  keywords: string[];
}[] = [
  {
    value: 'all',
    label: 'All Failures',
    icon: Filter,
    color: 'text-muted-foreground',
    activeBg: 'bg-foreground',
    activeText: 'text-white',
    activeBorder: 'border-foreground',
    keywords: [],
  },
  {
    value: 'invalid_number',
    label: 'Invalid Number',
    icon: PhoneOff,
    color: 'text-red-500',
    activeBg: 'bg-red-500',
    activeText: 'text-white',
    activeBorder: 'border-red-500',
    keywords: ['invalid_number', 'invalid', 'unregistered', 'not exist', 'does not exist', 'no such', 'unknown number'],
  },
  {
    value: 'delivery_timeout',
    label: 'Delivery Timeout',
    icon: TimerOff,
    color: 'text-orange-500',
    activeBg: 'bg-orange-500',
    activeText: 'text-white',
    activeBorder: 'border-orange-500',
    keywords: ['delivery_timeout', 'timeout', 'timed out', 'expired', 'unreachable', 'not reachable'],
  },
  {
    value: 'carrier_rejection',
    label: 'Carrier Rejection',
    icon: ShieldX,
    color: 'text-purple-500',
    activeBg: 'bg-purple-500',
    activeText: 'text-white',
    activeBorder: 'border-purple-500',
    keywords: ['carrier_rejection', 'carrier', 'rejected', 'spam', 'blocked', 'filtered', 'opt-out', 'opted out'],
  },
];

function matchesFailureReason(log: DeletedLeadLog, filter: FailureReasonFilter): boolean {
  if (filter === 'all') return true;
  const option = FAILURE_REASON_OPTIONS.find((o) => o.value === filter);
  if (!option) return true;
  const reason = (log.failure_reason ?? '').toLowerCase();
  return option.keywords.some((kw) => reason.includes(kw));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KPICard({
  icon: Icon,
  label,
  value,
  sub,
  iconBg,
  iconColor,
  onClick,
  active,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  iconBg: string;
  iconColor: string;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-xl p-5 flex items-start gap-4 transition-all duration-150 ${
        onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''
      } ${active ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon size={18} className={iconColor} />
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-0.5 leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {onClick && (
        <div className="ml-auto self-center opacity-0 group-hover:opacity-100">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </div>
      )}
    </div>
  );
}

function RetryStatusBadge({ status }: { status: RetryItem['retryStatus'] }) {
  if (status === 'retrying')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
        <Hourglass size={10} />
        Retrying
      </span>
    );
  if (status === 'pending')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
        <Clock size={10} />
        Pending
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
      <XCircle size={10} />
      Exhausted
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DeliveryFailureAnalyticsClient() {
  const [mounted, setMounted] = useState(false);
  const [logs, setLogs] = useState<DeletedLeadLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [campaignStats, setCampaignStats] = useState<CampaignStat[]>([]);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [activeKPI, setActiveKPI] = useState<'total' | 'today' | 'retry' | 'sources' | null>(null);
  const [failureReasonFilter, setFailureReasonFilter] = useState<FailureReasonFilter>('all');
  const [historyLead, setHistoryLead] = useState<{ phone: string; name: string | null } | null>(null);

  const trendRef = React.useRef<HTMLDivElement>(null);
  const retryRef = React.useRef<HTMLDivElement>(null);
  const sourceRef = React.useRef<HTMLDivElement>(null);
  const logsRef = React.useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleKPIClick = (kpi: 'total' | 'today' | 'retry' | 'sources') => {
    setActiveKPI(prev => prev === kpi ? null : kpi);
    if (kpi === 'total') scrollTo(logsRef);
    else if (kpi === 'today') scrollTo(trendRef);
    else if (kpi === 'retry') scrollTo(retryRef);
    else if (kpi === 'sources') scrollTo(sourceRef);
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data, error: fetchErr } = await supabase
        .from('deleted_leads_log')
        .select('*')
        .order('deleted_at', { ascending: false })
        .limit(5000);
      if (fetchErr) throw fetchErr;
      setLogs(data ?? []);
      setLastRefreshed(new Date());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaignStats = useCallback(async () => {
    setCampaignLoading(true);
    try {
      const supabase = createClient();
      // Fetch all campaigns
      const { data: campaigns, error: campErr } = await supabase
        .from('campaigns')
        .select('id, name, sent');
      if (campErr) throw campErr;

      if (!campaigns || campaigns.length === 0) {
        setCampaignStats([]);
        return;
      }

      // Fetch failed counts per campaign from drip_message_queue
      const { data: queueData, error: queueErr } = await supabase
        .from('drip_message_queue')
        .select('campaign_id, status')
        .in('status', ['sent', 'failed']);
      if (queueErr) throw queueErr;

      // Aggregate counts per campaign_id
      const sentMap: Record<string, number> = {};
      const failedMap: Record<string, number> = {};
      (queueData ?? []).forEach((row) => {
        if (row.status === 'sent') {
          sentMap[row.campaign_id] = (sentMap[row.campaign_id] ?? 0) + 1;
        } else if (row.status === 'failed') {
          failedMap[row.campaign_id] = (failedMap[row.campaign_id] ?? 0) + 1;
        }
      });

      const stats: CampaignStat[] = campaigns
        .map((c) => {
          const sentFromQueue = sentMap[c.id] ?? 0;
          const failedFromQueue = failedMap[c.id] ?? 0;
          // Use campaigns.sent as total if queue has no data, else use queue totals
          const total = sentFromQueue + failedFromQueue > 0
            ? sentFromQueue + failedFromQueue
            : (c.sent ?? 0);
          const failed = failedFromQueue;
          const failureRate = total > 0 ? Math.round((failed / total) * 100) : 0;
          return { campaign: c.name, total, failed, failureRate };
        })
        .filter((c) => c.total > 0 || c.failed > 0)
        .sort((a, b) => b.failureRate - a.failureRate);

      setCampaignStats(stats);
    } catch (err: unknown) {
      console.error('Failed to load campaign stats:', err);
    } finally {
      setCampaignLoading(false);
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    setMounted(true);
    fetchLogs();
    fetchCampaignStats();
    const interval = setInterval(() => {
      fetchLogs();
      fetchCampaignStats();
    }, 30_000);
    return () => clearInterval(interval);
  }, [fetchLogs, fetchCampaignStats]);

  const filteredLogs = mounted
    ? logs.filter((l) => matchesFailureReason(l, failureReasonFilter))
    : [];

  const trend = mounted ? buildTrend(filteredLogs) : [];
  const sourceStats = mounted ? buildSourceStats(filteredLogs) : [];
  const retryQueue = mounted ? buildRetryQueue(filteredLogs) : [];

  const todayCount = mounted ? filteredLogs.filter((l) => {
    const d = new Date(l.deleted_at);
    const now = new Date();
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  }).length : 0;

  const retryingCount = retryQueue.filter((r) => r.retryStatus === 'retrying').length;
  const pendingCount = retryQueue.filter((r) => r.retryStatus === 'pending').length;
  const exhaustedCount = retryQueue.filter((r) => r.retryStatus === 'exhausted').length;

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* ── Header ── */}
      <div className="px-6 pt-6 pb-4 border-b border-border bg-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <TrendingDown size={18} className="text-red-500" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground leading-tight">
                Delivery Failure Analytics
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Real-time failure trends · retry queue · source & campaign breakdown
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              Last updated:{' '}
              {lastRefreshed
                ? (() => {
                    const h = lastRefreshed.getHours();
                    const m = String(lastRefreshed.getMinutes()).padStart(2, '0');
                    const s = String(lastRefreshed.getSeconds()).padStart(2, '0');
                    const ampm = h >= 12 ? 'pm' : 'am';
                    const h12 = h % 12 || 12;
                    return `${String(h12).padStart(2, '0')}:${m}:${s} ${ampm}`;
                  })()
                : '—'}
            </span>
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* ── Failure Reason Filter Bar ── */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-muted-foreground mr-1 flex items-center gap-1">
            <Filter size={12} />
            Filter by reason:
          </span>
          {FAILURE_REASON_OPTIONS.map((opt) => {
            const isActive = failureReasonFilter === opt.value;
            const IconComp = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => setFailureReasonFilter(opt.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 ${
                  isActive
                    ? `${opt.activeBg} ${opt.activeText} ${opt.activeBorder} shadow-sm`
                    : `bg-white border-border text-muted-foreground hover:border-gray-300 hover:bg-muted/30`
                }`}
              >
                <IconComp size={11} className={isActive ? 'text-white' : opt.color} />
                {opt.label}
                {opt.value !== 'all' && mounted && (
                  <span
                    className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {logs.filter((l) => matchesFailureReason(l, opt.value)).length}
                  </span>
                )}
              </button>
            );
          })}
          {failureReasonFilter !== 'all' && (
            <span className="text-xs text-muted-foreground ml-1">
              Showing{' '}
              <span className="font-semibold text-foreground">{filteredLogs.length}</span> of{' '}
              {logs.length} failures
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 px-6 py-5 space-y-6">
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* ── KPI Row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            icon={XCircle}
            label="Total Failures"
            value={filteredLogs.length}
            sub={failureReasonFilter === 'all' ? 'All time auto-deletions' : `Filtered: ${FAILURE_REASON_OPTIONS.find(o => o.value === failureReasonFilter)?.label}`}
            iconBg="bg-red-50"
            iconColor="text-red-500"
            onClick={() => handleKPIClick('total')}
            active={activeKPI === 'total'}
          />
          <KPICard
            icon={Activity}
            label="Failures Today"
            value={todayCount}
            sub="Since midnight"
            iconBg="bg-orange-50"
            iconColor="text-orange-500"
            onClick={() => handleKPIClick('today')}
            active={activeKPI === 'today'}
          />
          <KPICard
            icon={Hourglass}
            label="Retry Queue"
            value={retryingCount + pendingCount}
            sub={`${retryingCount} retrying · ${pendingCount} pending`}
            iconBg="bg-blue-50"
            iconColor="text-blue-500"
            onClick={() => handleKPIClick('retry')}
            active={activeKPI === 'retry'}
          />
          <KPICard
            icon={Layers}
            label="Sources Affected"
            value={sourceStats.length}
            sub="Distinct lead sources"
            iconBg="bg-purple-50"
            iconColor="text-purple-500"
            onClick={() => handleKPIClick('sources')}
            active={activeKPI === 'sources'}
          />
        </div>

        {/* ── Trend Chart + Retry Queue ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Failure Trend */}
          <div className="xl:col-span-2 bg-white border border-border rounded-xl p-5" ref={trendRef}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-foreground">Delivery Failure Trend</h2>
              <span className="text-xs text-muted-foreground">Last 14 days</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Daily count of leads auto-deleted due to WhatsApp delivery failure
            </p>
            {loading ? (
              <div className="h-52 flex items-center justify-center text-muted-foreground text-sm gap-2">
                <RefreshCw size={14} className="animate-spin" /> Loading…
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="failGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={false}
                    interval={1}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                    formatter={(v: number) => [v, 'Failures']}
                  />
                  <Area
                    type="monotone"
                    dataKey="failures"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fill="url(#failGrad)"
                    dot={{ r: 3, fill: '#ef4444', strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Retry Queue */}
          <div className="bg-white border border-border rounded-xl p-5 flex flex-col" ref={retryRef}>
            <div className="flex items-center gap-2 mb-1">
              <Hourglass size={15} className="text-blue-500" />
              <h2 className="text-sm font-semibold text-foreground">Retry Queue</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Recent failures pending re-delivery attempt
            </p>
            {/* Queue summary pills */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                <Hourglass size={10} /> {retryingCount} Retrying
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
                <Clock size={10} /> {pendingCount} Pending
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                <XCircle size={10} /> {exhaustedCount} Exhausted
              </span>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 max-h-48">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-xs gap-2">
                  <RefreshCw size={12} className="animate-spin" /> Loading…
                </div>
              ) : retryQueue.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <CheckCircle2 size={24} className="text-green-400 mb-2" />
                  <p className="text-xs text-muted-foreground">Queue is empty</p>
                </div>
              ) : (
                retryQueue.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-muted/30 border border-border"
                  >
                    <div className="min-w-0">
                      <button
                        onClick={() => setHistoryLead({ phone: item.phone, name: item.lead_name })}
                        className="text-xs font-medium text-foreground truncate hover:text-blue-600 hover:underline transition-colors text-left block"
                      >
                        {item.lead_name ?? item.phone}
                      </button>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {item.source ?? '—'} · {formatTime(item.deleted_at)}
                      </p>
                    </div>
                    <RetryStatusBadge status={item.retryStatus} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Affected Leads by Source + Campaign Comparison ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Affected Leads by Source */}
          <div className="bg-white border border-border rounded-xl p-5" ref={sourceRef}>
            <div className="flex items-center gap-2 mb-1">
              <BarChart2 size={15} className="text-purple-500" />
              <h2 className="text-sm font-semibold text-foreground">Affected Leads by Source</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Breakdown of delivery failures per lead source
            </p>
            {loading ? (
              <div className="h-52 flex items-center justify-center text-muted-foreground text-sm gap-2">
                <RefreshCw size={14} className="animate-spin" /> Loading…
              </div>
            ) : sourceStats.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-muted-foreground text-sm">
                No data available
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart
                    data={sourceStats}
                    layout="vertical"
                    margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="source"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      tickLine={false}
                      axisLine={false}
                      width={110}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                      }}
                      formatter={(v: number) => [v, 'Failures']}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={20}>
                      {sourceStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {sourceStats.map((s) => (
                    <span
                      key={s.source}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                        style={{ backgroundColor: s.fill }}
                      />
                      {s.source} ({s.count})
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Failure Rate by Campaign */}
          <div className="bg-white border border-border rounded-xl p-5" ref={logsRef}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown size={15} className="text-orange-500" />
              <h2 className="text-sm font-semibold text-foreground">Failure Rate by Campaign</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Percentage of messages that failed per campaign
            </p>
            {campaignLoading ? (
              <div className="h-52 flex items-center justify-center text-muted-foreground text-sm gap-2">
                <RefreshCw size={14} className="animate-spin" /> Loading…
              </div>
            ) : campaignStats.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-muted-foreground text-sm">
                No campaign data available
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart
                    data={campaignStats}
                    margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="campaign"
                      tick={{ fontSize: 9, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      angle={-15}
                      textAnchor="end"
                      height={40}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                      unit="%"
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                      }}
                      formatter={(v: number, name: string) => [
                        name === 'failureRate' ? `${v}%` : v,
                        name === 'failureRate' ? 'Failure Rate' : 'Failed',
                      ]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                      formatter={(value) =>
                        value === 'failureRate' ? 'Failure Rate (%)' : 'Failed Leads'
                      }
                    />
                    <Bar dataKey="failureRate" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={32} />
                    <Bar dataKey="failed" fill="#fca5a5" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
                {/* Campaign table */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1.5 text-muted-foreground font-medium">Campaign</th>
                        <th className="text-right py-1.5 text-muted-foreground font-medium">Sent</th>
                        <th className="text-right py-1.5 text-muted-foreground font-medium">Failed</th>
                        <th className="text-right py-1.5 text-muted-foreground font-medium">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {campaignStats.map((c) => (
                        <tr key={c.campaign} className="hover:bg-muted/20 transition-colors">
                          <td className="py-1.5 text-foreground font-medium truncate max-w-[120px]">
                            {c.campaign}
                          </td>
                          <td className="py-1.5 text-right text-muted-foreground">{c.total}</td>
                          <td className="py-1.5 text-right text-red-600 font-medium">{c.failed}</td>
                          <td className="py-1.5 text-right">
                            <span
                              className={`font-semibold ${
                                c.failureRate > 15
                                  ? 'text-red-600'
                                  : c.failureRate > 8
                                  ? 'text-orange-500' :'text-green-600'
                              }`}
                            >
                              {c.failureRate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Failure Logs Table ── */}
        <div className="bg-white border border-border rounded-xl overflow-hidden" ref={logsRef}>
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Failure Logs</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {filteredLogs.length} auto-deleted lead{filteredLogs.length !== 1 ? 's' : ''} due to delivery failure
              </p>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
              <RefreshCw size={14} className="animate-spin" /> Loading failure logs…
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <CheckCircle2 size={32} className="text-green-400" />
              <p className="text-sm text-muted-foreground">No failures found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Phone</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Source</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Failure Reason</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Deleted At</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredLogs.slice(0, 200).map((log) => (
                    <tr key={log.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setHistoryLead({ phone: log.phone, name: log.lead_name })}
                          className="font-medium text-foreground text-sm hover:text-blue-600 hover:underline transition-colors text-left"
                        >
                          {log.lead_name ?? <span className="text-muted-foreground italic">Unknown</span>}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-muted-foreground">{log.phone}</span>
                      </td>
                      <td className="px-4 py-3">
                        {log.source ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                            style={{
                              backgroundColor: `${getSourceColor(log.source)}20`,
                              color: getSourceColor(log.source),
                            }}
                          >
                            {log.source}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-red-600 font-medium">{log.failure_reason ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {mounted ? formatTime(log.deleted_at) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setHistoryLead({ phone: log.phone, name: log.lead_name })}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 hover:text-foreground transition-colors"
                          title="View full message history"
                        >
                          <History size={11} />
                          History
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {historyLead && (
        <LeadMessageHistoryModal
          phone={historyLead.phone}
          leadName={historyLead.name}
          onClose={() => setHistoryLead(null)}
        />
      )}
    </div>
  );
}
